import { useState } from 'react';
import { Card, SLabel, WBtn } from '../components/Atoms';
import { C } from '../constants';
import { fmtDay } from '../utils';
import ParseLabsModal from './ParseLabsModal';

const EMPTY_ENTRY = () => ({ id: `e${Date.now()}-${Math.random()}`, name: '', value: '', unit: '', flag: '' });

// Lab panels — grouped sets
const LAB_PANELS = [
  { name: 'CBC',   tests: ['Hgb','Hct','WBC','Plt','Neu','Mon','Lym','Eos'] },
  { name: 'BMP',   tests: ['Na','K','Crea','HCO3','Cl','BUN','RBS'] },
  { name: 'COAGS', tests: ['PT','PTT','%','INR'] },
  { name: 'LIVER', tests: ['AST','ALT','ALP','TB','IB','DB'] },
  { name: 'RENAL', tests: ['Ca','Mg','Phos'] },
  { name: 'LIPID', tests: ['TC','Trig','HDL','LDL','C/L Ratio'] },
  { name: 'DM',    tests: ['FBS','HbA1c'] },
  { name: 'ABG',   tests: ['pH','PaCO2','PaO2','HCO3','O2 Sat','Base Excess'] },
  { name: 'UA',    tests: ['pH','SG','Glucose','Blood','Ketones','Protein','WBC','RBC','Epithelial'] },
];

const PANEL_COLORS = {
  CBC: '#4A9EFF', BMP: '#30D158', COAGS: '#FF6B6B', LIVER: '#FF9500',
  RENAL: '#64D2FF', LIPID: '#BF5AF2', DM: '#FF375F', ABG: '#FFD60A', UA: '#6E6E73',
};

export default function LabsTab({ cur, ldate, setLdate, ltime, setLtime, addLabSet, addLabRow, editLab, delLabRow, delLabSet, saveLabSet, clearLabFlag }) {
  const [entries, setEntries]       = useState([EMPTY_ENTRY()]);
  const [customName, setCustomName] = useState('');
  const [activePanel, setActivePanel] = useState(null);
  const [editingSetId, setEditingSetId] = useState(null);
  const [editSetEntries, setEditSetEntries] = useState([]);
  const [editSetDate, setEditSetDate] = useState('');
  const [editSetTime, setEditSetTime] = useState('');
  const [showParse, setShowParse] = useState(false);
  const [showForm,  setShowForm]  = useState(false);

  const llog = [...(cur.labsLog || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));

  const openEditSet = (g) => {
    setEditingSetId(g.id);
    setEditSetEntries(g.entries.map(e => ({ ...e })));
    setEditSetDate(g.date);
    setEditSetTime(g.time);
  };
  const setEditEntry = (id, field, val) => setEditSetEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: val } : e));
  const removeEditEntry = (id) => setEditSetEntries(prev => prev.filter(e => e.id !== id));
  const addEditEntry = () => setEditSetEntries(prev => [...prev, { ...EMPTY_ENTRY() }]);
  const saveEditSet = () => {
    saveLabSet(editingSetId, editSetEntries.filter(e => e.name), editSetDate, editSetTime);
    setEditingSetId(null);
  };

  const setEntry   = (id, field, val) => setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: val } : e));
  const removeRow  = (id) => setEntries(prev => prev.filter(e => e.id !== id));

  // Add individual preset
  const addPreset = (name) => {
    if (entries.some(e => e.name === name)) return;
    setEntries(prev => {
      const empty = prev.find(e => !e.name);
      if (empty) return prev.map(e => e.id === empty.id ? { ...e, name } : e);
      return [...prev, { ...EMPTY_ENTRY(), name }];
    });
  };

  // Add full panel — skips duplicates
  const addPanel = (panel) => {
    setEntries(prev => {
      let updated = [...prev];
      panel.tests.forEach(name => {
        if (updated.some(e => e.name === name)) return;
        const empty = updated.find(e => !e.name);
        if (empty) {
          updated = updated.map(e => e.id === empty.id ? { ...e, name } : e);
        } else {
          updated = [...updated, { ...EMPTY_ENTRY(), name }];
        }
      });
      return updated;
    });
    setActivePanel(null);
  };

  const addCustom = () => {
    const n = customName.trim();
    if (!n) return;
    setEntries(prev => {
      const empty = prev.find(e => !e.name);
      if (empty) return prev.map(e => e.id === empty.id ? { ...e, name: n } : e);
      return [...prev, { ...EMPTY_ENTRY(), name: n }];
    });
    setCustomName('');
  };

  const saveSet = () => {
    const valid = entries.filter(e => e.name && e.value);
    if (!valid.length) return;
    addLabSet('__batch__', valid, ldate, ltime);
    setEntries([EMPTY_ENTRY()]);
    setActivePanel(null);
  };

  const confirmDelSet = (g) => {
    if (!window.confirm(`Delete the ${g.time} lab set? This cannot be undone.`)) return;
    delLabSet(g.id);
  };

  const confirmDelRow = (gid, eid, name) => {
    if (!window.confirm(`Delete "${name || 'this result'}"?`)) return;
    delLabRow(gid, eid);
  };

  const allTests = [...new Set(llog.flatMap(g => g.entries.map(e => e.name)).filter(Boolean))];
  // Sort by panel order, then by position within panel, unknowns go last
  const panelOrder = LAB_PANELS.flatMap(p => p.tests);
  const sortedTests = allTests.sort((a, b) => {
    const ia = panelOrder.indexOf(a), ib = panelOrder.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  const summaryData = sortedTests.map(name => {
    const vals = llog.flatMap(g => g.entries.filter(e => e.name === name).map(e => ({ ...e, date: g.date, time: g.time }))).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
    return { name, vals: vals.slice(0, 4) };
  }).filter(row => row.vals.length > 0);

  const namedEntries = entries.filter(e => e.name);

  return (
    <>
      {showParse && (
        <ParseLabsModal
          onClose={() => setShowParse(false)}
          onParsed={(results) => {
            if (results.length > 0) {
              addLabSet('__batch__', results, ldate, ltime);
            }
          }}
        />
      )}

      {!showForm && (
        <button onClick={() => { setShowForm(true); setEntries([EMPTY_ENTRY()]); setCustomName(''); setActivePanel(null); }} style={{
          width: '100%', padding: '11px 0', borderRadius: 10, border: '1px dashed #2A4A6A',
          background: 'transparent', color: '#4A9EFF', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          ＋ New Lab Entry
        </button>
      )}

      {showForm && (
      <Card s={{ border: '1px solid #2A4A6A' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <SLabel color="#4A9EFF" mb={0}>New Lab Entry</SLabel>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <input type="date" value={ldate} onChange={e => setLdate(e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
            <input type="time" value={ltime} onChange={e => setLtime(e.target.value)}
              style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
          </div>
        </div>

        {/* Panel buttons */}
        <div style={{ fontSize: 9, fontWeight: 800, color: C.dim2, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Quick Add Panel</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {LAB_PANELS.map((panel, panelIdx) => {
            const color = PANEL_COLORS[panel.name] || '#AAA';
            const inList = panel.tests.every(t => entries.some(e => e.name === t));
            const partial = !inList && panel.tests.some(t => entries.some(e => e.name === t));
            const isOpen = activePanel === panel.name;
            const dropAlign = ['CBC','BMP','COAGS','RENAL'].includes(panel.name) ? { left: 0 } : { right: 0 };
            return (
              <div key={panel.name} style={{ position: 'relative' }}>
                <button onClick={() => setActivePanel(isOpen ? null : panel.name)}
                  style={{ padding: '6px 12px', borderRadius: 8, border: `1.5px solid ${inList || partial ? color : C.bd}`, background: inList ? color + '22' : partial ? color + '11' : C.bg3, color: inList || partial ? color : C.dim, fontSize: 12, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  {panel.name}
                  {partial && <span style={{ fontSize: 8, background: color + '33', borderRadius: 4, padding: '1px 4px' }}>partial</span>}
                  <span style={{ fontSize: 9, opacity: 0.6 }}>{isOpen ? '▲' : '▼'}</span>
                </button>

                {/* Dropdown: individual test toggle + Add All */}
                {isOpen && (
                  <div style={{ position: 'absolute', top: '100%', ...dropAlign, zIndex: 20, background: C.bg1, border: `1.5px solid ${color}44`, borderRadius: 10, padding: '10px', marginTop: 4, minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{panel.name} Tests</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                      {panel.tests.map(test => {
                        const added = entries.some(e => e.name === test);
                        return (
                          <button key={test} onClick={() => added ? removeRow(entries.find(e => e.name === test)?.id) : addPreset(test)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 6, border: `1px solid ${added ? color : C.bd}`, background: added ? color + '22' : 'transparent', color: added ? color : C.tx, fontSize: 12, fontWeight: added ? 700 : 400, cursor: 'pointer', textAlign: 'left' }}>
                            <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${added ? color : C.dim}`, background: added ? color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {added && <span style={{ fontSize: 8, color: '#000', fontWeight: 900 }}>✓</span>}
                            </span>
                            {test}
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => addPanel(panel)}
                      style={{ width: '100%', padding: '7px 0', borderRadius: 8, background: color + '22', border: `1px solid ${color}55`, color, fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                      + Add All
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Custom test input */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          <input value={customName} onChange={e => setCustomName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustom()}
            placeholder="Custom test name…"
            style={{ flex: 1, background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12 }} />
          <button onClick={addCustom}
            style={{ background: '#1A3D6A', border: 'none', borderRadius: 6, color: '#7DC4FF', fontSize: 12, padding: '6px 12px', cursor: 'pointer', fontWeight: 700 }}>+ Add</button>
        </div>

        {/* Entry rows */}
        {namedEntries.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 'auto' }} />
                <col style={{ width: 'auto' }} />
                <col style={{ width: 60 }} />
                <col style={{ width: 48 }} />
                <col style={{ width: 28 }} />
              </colgroup>
              <thead>
                <tr>
                  {['Test', 'Value', 'Unit', 'Flag', ''].map((h, i) => (
                    <th key={i} style={{ fontSize: 8, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', paddingLeft: 4, paddingBottom: 4, textAlign: 'left', fontFamily: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {namedEntries.map(e => (
                  <tr key={e.id}>
                    <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                      <div style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
                    </td>
                    <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                      <input value={e.value} onChange={ev => setEntry(e.id, 'value', ev.target.value)} placeholder="—"
                        style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1.5px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: '#E0EEFA', fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }} />
                    </td>
                    <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                      <input value={e.unit} onChange={ev => setEntry(e.id, 'unit', ev.target.value)} placeholder="unit"
                        style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 4px', color: C.dim, fontSize: 11 }} />
                    </td>
                    <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                      <select value={e.flag} onChange={ev => setEntry(e.id, 'flag', ev.target.value)}
                        style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, color: e.flag === 'H' ? '#FF4545' : e.flag === 'L' ? '#64D2FF' : C.tx, fontSize: 12, padding: '5px 2px', fontWeight: 700 }}>
                        <option value="">—</option><option value="H">H</option><option value="L">L</option>
                      </select>
                    </td>
                    <td style={{ paddingBottom: 5, textAlign: 'center' }}>
                      <button onClick={() => removeRow(e.id)}
                        style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {namedEntries.length > 0
          ? <WBtn onClick={saveSet} bg="#1A3D6A">💾 Save Lab Set ({namedEntries.length} tests)</WBtn>
          : <div style={{ textAlign: 'center', fontSize: 12, color: C.dim2, padding: '4px 0' }}>Select a panel or add custom tests above</div>
        }
        <button onClick={() => setShowParse(true)}
          style={{ width: '100%', marginTop: 8, padding: '10px 0', borderRadius: 10, background: 'transparent', border: '1px dashed #4A9EFF55', color: '#4A9EFF', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          📋 Paste Labs
        </button>
        <button onClick={() => { setShowForm(false); setEntries([EMPTY_ENTRY()]); setCustomName(''); setActivePanel(null); }}
          style={{ width: '100%', marginTop: 6, padding: '7px 0', borderRadius: 10, background: 'transparent', border: 'none', color: C.dim2, fontSize: 12, cursor: 'pointer' }}>
          ✕ Cancel
        </button>
      </Card>
      )}

      {/* Backdrop

      {/* Lab Trends Summary */}
      {summaryData.length > 0 && (
        <div style={{ background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.bd}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: '#4A9EFF', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Lab Trends</span>
            <span style={{ fontSize: 10, color: C.dim2 }}>latest → oldest</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {summaryData.map((row, idx) => {
                  const panel = LAB_PANELS.find(p => p.tests.includes(row.name));
                  const prevPanel = idx > 0 ? LAB_PANELS.find(p => p.tests.includes(summaryData[idx-1].name)) : null;
                  const showHeader = panel && panel !== prevPanel;
                  const panelColor = panel ? (PANEL_COLORS[panel.name] || '#AAA') : null;
                  return (
                    <>
                      {showHeader && (
                        <tr key={`hdr-${panel.name}`}>
                          <td colSpan={5} style={{ padding: '4px 12px 2px', background: panelColor + '18', borderTop: idx > 0 ? `2px solid ${panelColor}33` : undefined }}>
                            <span style={{ fontSize: 8, fontWeight: 900, color: panelColor, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{panel.name}</span>
                          </td>
                        </tr>
                      )}
                      <tr key={row.name} style={{ borderBottom: `1px solid ${C.bg0}` }}>
                        <td style={{ padding: '7px 12px', fontWeight: 700, color: C.tx, whiteSpace: 'nowrap', borderRight: `1px solid ${C.bg0}`, background: C.bg3, minWidth: 70 }}>{row.name}</td>
                        {row.vals.map((v, i) => {
                          const fc = v.flag === 'H' ? '#FF4545' : v.flag === 'L' ? '#64D2FF' : null;
                          const prev = row.vals[i + 1];
                          const trend = v.value && prev?.value && v.value !== prev.value
                            ? (parseFloat(v.value) > parseFloat(prev.value) ? '↑' : '↓') : null;
                          return (
                            <td key={v.id} style={{ padding: '7px 10px', textAlign: 'center', background: i === 0 ? '#0A1E2E' : undefined }}>
                              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontWeight: i === 0 ? 700 : 400, color: fc || (i === 0 ? '#E0EEFA' : C.dim), display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                                {v.value}
                                {trend && <span style={{ fontSize: 9, color: trend === '↑' ? '#FF4545' : '#30D158' }}>{trend}</span>}
                                {v.flag && <span style={{ fontSize: 9, fontWeight: 800, color: fc }}>{v.flag}</span>}
                              </div>
                              <div style={{ fontSize: 8, color: C.dim2, marginTop: 1 }}>{v.date?.slice(5)}</div>
                            </td>
                          );
                        })}
                        {[...Array(Math.max(0, 4 - row.vals.length))].map((_, i) => (
                          <td key={`empty-${i}`} style={{ padding: '7px 10px', textAlign: 'center', color: C.dim2 }}>—</td>
                        ))}
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}




      {/* Backdrop to close panel dropdown */}
      {activePanel && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setActivePanel(null)} />
      )}

      {/* History */}
      {llog.length === 0 && <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: 20 }}>No labs recorded</div>}
      {llog.map(g => {
        const isEditing = editingSetId === g.id;
        return (
          <Card key={g.id} s={{ marginBottom: 10, border: isEditing ? '1px solid #4A9EFF' : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              {isEditing ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                  <input type="date" value={editSetDate} onChange={e => setEditSetDate(e.target.value)}
                    style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
                  <input type="time" value={editSetTime} onChange={e => setEditSetTime(e.target.value)}
                    style={{ background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '4px 6px', color: C.tx, fontSize: 11, colorScheme: 'dark' }} />
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#4A9EFF' }}>{fmtDay(g.date)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.tx }}>{g.time}</div>
                </div>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {isEditing ? (
                  <>
                    <button onClick={saveEditSet}
                      style={{ background: '#081A0C', border: '1px solid #1A3A1E', borderRadius: 6, color: '#30D158', cursor: 'pointer', fontSize: 11, padding: '4px 10px', fontWeight: 700 }}>
                      ✓ Save
                    </button>
                    <button onClick={() => setEditingSetId(null)}
                      style={{ background: C.bg2, border: `1px solid ${C.bd}`, borderRadius: 6, color: C.dim, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openEditSet(g)}
                      style={{ background: '#0A1E2E', border: '1px solid #1A3D6A', borderRadius: 6, color: '#4A9EFF', cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
                      ✏ Edit
                    </button>
                    <button onClick={() => confirmDelSet(g)}
                      style={{ background: '#1A0808', border: '1px solid #3A1515', borderRadius: 6, color: '#FF6B6B', cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
                      🗑
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Edit mode rows */}
            {isEditing ? (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: 4 }}>
                  <colgroup>
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: 'auto' }} />
                    <col style={{ width: 60 }} />
                    <col style={{ width: 48 }} />
                    <col style={{ width: 28 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {['Test', 'Value', 'Unit', 'Flag', ''].map((h, i) => (
                        <th key={i} style={{ fontSize: 8, color: C.dim2, fontWeight: 800, textTransform: 'uppercase', paddingLeft: 4, paddingBottom: 4, textAlign: 'left', fontFamily: 'inherit' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {editSetEntries.map(e => (
                      <tr key={e.id}>
                        <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                          <input value={e.name} onChange={ev => setEditEntry(e.id, 'name', ev.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: C.tx, fontSize: 12, fontWeight: 600 }} />
                        </td>
                        <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                          <input value={e.value} onChange={ev => setEditEntry(e.id, 'value', ev.target.value)} placeholder="—"
                            style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1.5px solid ${C.bd}`, borderRadius: 6, padding: '6px 8px', color: '#E0EEFA', fontSize: 13, fontFamily: "'IBM Plex Mono',monospace" }} />
                        </td>
                        <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                          <input value={e.unit} onChange={ev => setEditEntry(e.id, 'unit', ev.target.value)} placeholder="unit"
                            style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, padding: '6px 4px', color: C.dim, fontSize: 11 }} />
                        </td>
                        <td style={{ paddingRight: 5, paddingBottom: 5 }}>
                          <select value={e.flag} onChange={ev => setEditEntry(e.id, 'flag', ev.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', background: C.bg3, border: `1px solid ${C.bd}`, borderRadius: 6, color: e.flag === 'H' ? '#FF4545' : e.flag === 'L' ? '#64D2FF' : C.tx, fontSize: 12, padding: '5px 2px', fontWeight: 700 }}>
                            <option value="">—</option><option value="H">H</option><option value="L">L</option>
                          </select>
                        </td>
                        <td style={{ paddingBottom: 5, textAlign: 'center' }}>
                          <button onClick={() => removeEditEntry(e.id)}
                            style={{ background: 'none', border: 'none', color: '#FF4545', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                ))}
                <button onClick={addEditEntry}
                  style={{ background: C.bg3, border: `1px dashed ${C.bd}`, borderRadius: 6, color: C.dim, fontSize: 12, padding: '6px 0', cursor: 'pointer', width: '100%', marginTop: 4 }}>
                  + Add Row
                </button>
              </>
            ) : (
              <>
                {g.entries.length === 0 && <div style={{ color: C.dim2, fontSize: 12 }}>No entries</div>}
                {(() => {
                  // Group entries by panel, preserving order
                  const panelOrder = LAB_PANELS.flatMap(p => p.tests);
                  const sorted = [...g.entries].sort((a, b) => {
                    const ia = panelOrder.indexOf(a.name), ib = panelOrder.indexOf(b.name);
                    if (ia === -1 && ib === -1) return 0;
                    if (ia === -1) return 1;
                    if (ib === -1) return -1;
                    return ia - ib;
                  });
                  let lastPanel = null;
                  return sorted.map(e => {
                    const panel = LAB_PANELS.find(p => p.tests.includes(e.name));
                    const panelName = panel?.name || null;
                    const showLabel = panelName && panelName !== lastPanel;
                    if (showLabel) lastPanel = panelName;
                    const panelColor = panel ? (PANEL_COLORS[panelName] || '#AAA') : null;
                    const fc = e.flag === 'H' ? '#FF4545' : e.flag === 'L' ? '#64D2FF' : null;
                    return (
                      <div key={e.id}>
                        {showLabel && (
                          <div style={{ fontSize: 8, fontWeight: 900, color: panelColor, textTransform: 'uppercase', letterSpacing: '0.12em', padding: '6px 0 2px', borderTop: lastPanel !== panelName ? undefined : '1px solid ' + panelColor + '22' }}>
                            {panelName}
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(60px,auto) auto 1fr auto auto', alignItems: 'center', gap: '0 10px', padding: '5px 0', borderBottom: `1px solid ${C.bg0}` }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.tx }}>{e.name}</div>
                          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 15, fontWeight: 700, color: fc || '#E0EEFA' }}>{e.value}</div>
                          <div style={{ fontSize: 10, color: C.dim }}>{e.unit || ''}</div>
                          <div>
                            {e.flag && (
                              <button onClick={() => clearLabFlag(g.id, e.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 800, color: fc, background: fc + '22', border: `1px solid ${fc}44`, borderRadius: 4, padding: '1px 5px', cursor: 'pointer' }}>
                                {e.flag} ×
                              </button>
                            )}
                          </div>
                          <button onClick={() => confirmDelRow(g.id, e.id, e.name)}
                            style={{ background: 'none', border: 'none', color: C.dim2, cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>×</button>
                        </div>
                      </div>
                    );
                  });
                })()}
              </>
            )}
          </Card>
        );
      })}
    </>
  );
}
